import { Navigation } from '../home/components/Navigation';
import { Footer } from '../home/components/Footer';
import { useEffect } from 'react';
import { SEO } from '../../components/SEO';

export default function ServiceAgreementPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Service Agreement | AI Receptionist Service"
        description="Read our service agreement to understand the terms and conditions of our AI receptionist and phone answering service offerings."
        canonical="/service-agreement"
        schemas={[
          {
            type: 'WebPage',
            data: {
              '@type': 'WebPage',
              name: 'Service Agreement',
              description: 'Service agreement for AI Receptionist Service',
              url: `${siteUrl}/service-agreement`,
              publisher: {
                '@type': 'Organization',
                name: 'AI Receptionist Service',
                url: siteUrl
              }
            }
          }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />
        
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-4">
              Dino Nuggets Service Agreement
            </h1>
            <p className="text-gray-600 mb-12">
              Effective Date: Date of purchase
            </p>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-8">
                This Service Agreement ("Agreement") is entered into between Dino Nuggets LLC ("Company," "we," or "us") and the purchaser ("Client" or "you") upon completion of checkout and acceptance of these terms.
              </p>
              <p className="text-gray-700 leading-relaxed mb-10">
                <strong>By purchasing any Dino Nuggets service package, Client agrees to be legally bound by this Agreement.</strong>
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  1. Services Provided
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Dino Nuggets provides AI-powered business automation, website support, and marketing technology services. Specific services depend on the package selected at checkout.
                </p>
                
                <h3 className="text-xl font-semibold text-secondary mb-3 mt-6">
                  Available Packages
                </h3>
                
                <div className="space-y-6">
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-secondary mb-2">
                      Hatchling Package — $197/month
                    </h4>
                    <ul className="list-disc pl-6 text-gray-700 space-y-1">
                      <li>Website support</li>
                      <li>Basic automation tools</li>
                      <li>Conversation AI assistant</li>
                      <li>Scheduling tools</li>
                      <li>QR code forms</li>
                    </ul>
                  </div>

                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-secondary mb-2">
                      Core Package — $350/month
                    </h4>
                    <ul className="list-disc pl-6 text-gray-700 space-y-1">
                      <li>Website design & hosting support</li>
                      <li>Voice AI assistant</li>
                      <li>Conversation AI assistant</li>
                      <li>Automated calendar/scheduling</li>
                      <li>Social media automations</li>
                      <li>QR code forms</li>
                    </ul>
                  </div>

                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-secondary mb-2">
                      Titan Package — $697/month
                    </h4>
                    <p className="text-gray-700">
                      Includes all Core features plus expanded automation, priority support, and advanced tools (as described on the sales page)
                    </p>
                  </div>
                </div>

                <p className="text-gray-700 leading-relaxed mt-6">
                  Services provided will correspond to the package selected at the time of purchase.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Company reserves the right to enhance or modify features provided that overall service functionality remains substantially similar.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  2. Add-On Services
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Optional add-ons may include:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Facebook Ads Management</li>
                  <li>Google Ads Management</li>
                  <li>Online Store / E-commerce Setup</li>
                  <li>Custom integrations or development</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Add-ons may carry additional monthly or one-time fees as disclosed at purchase.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  3. Fees & Billing
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Client agrees to pay the subscription fee associated with the selected package.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Billing occurs automatically on a recurring monthly basis via Stripe</li>
                  <li>Charges begin on the date of purchase</li>
                  <li>Fees are billed in advance</li>
                  <li>Failure of payment may result in suspension or termination of services</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>All fees are non-refundable once services have begun.</strong>
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  4. Minimum Commitment
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All Dino Nuggets service packages require a <strong>minimum initial commitment of three (3) months</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Client agrees to complete the full three-month term before cancellation is permitted.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  If Client cancels prior to completing the minimum term, Client remains responsible for payment of the remaining balance due for the initial commitment period.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  5. Term & Cancellation
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  After the initial 3-month term, services continue on a month-to-month basis.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Client may cancel at any time after the initial term by providing written notice.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Cancellation becomes effective at the end of the current billing cycle</li>
                  <li>No prorated refunds will be issued</li>
                  <li>Services remain active through the paid period</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Upon termination:</strong>
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Hosting, AI assistants, automations, and systems may be disabled</li>
                  <li>Company is not obligated to transfer proprietary systems or configurations</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  6. Client Responsibilities
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Client agrees to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Provide accurate business information and materials</li>
                  <li>Maintain legal compliance in their industry</li>
                  <li>Obtain necessary consent before contacting customers</li>
                  <li>Respond to requests needed for setup or operation</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Company is not responsible for delays caused by lack of client cooperation.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  7. No Guarantee of Results
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Company provides technology tools, automation systems, and marketing support.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Client acknowledges:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Results are not guaranteed</li>
                  <li>Business outcomes depend on many external factors</li>
                  <li>Company does not guarantee revenue, leads, rankings, or performance metrics</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  8. Intellectual Property
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Client retains ownership of their business name, branding, and content provided.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Company retains ownership of:</strong>
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Automation systems</li>
                  <li>AI workflows</li>
                  <li>Templates</li>
                  <li>Software configurations</li>
                  <li>Internal frameworks</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  These proprietary assets are not transferred upon cancellation unless agreed in writing.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  9. Third-Party Platforms
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Services rely on third-party providers including, but not limited to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>GoHighLevel</li>
                  <li>Stripe</li>
                  <li>Hosting providers</li>
                  <li>AI platforms</li>
                  <li>SMS and email carriers</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Company is not responsible for outages, policy changes, or service disruptions caused by third parties.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  10. Compliance & Communications Laws
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Client is solely responsible for compliance with applicable laws, including:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>TCPA (Telephone Consumer Protection Act)</li>
                  <li>CAN-SPAM Act</li>
                  <li>Data privacy regulations</li>
                  <li>Industry-specific regulations</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Client agrees to obtain proper consent before sending SMS, email, or automated communications.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Company is not liable for Client misuse of communication tools.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  11. Limitation of Liability
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To the fullest extent permitted by law:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Company shall not be liable for any indirect, incidental, or consequential damages, including lost profits or business interruption.</li>
                  <li>Company's total liability shall not exceed the amount paid by Client during the preceding 30 days.</li>
                </ul>
              </section>

               <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  12. Dispute Resolution
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This Agreement shall be governed by the laws of the State of Michigan.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Any disputes shall be resolved in the appropriate courts within Michigan.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  13. Modifications
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Company reserves the right to update this Agreement at any time. Updated versions will apply to continued use of services after notice is provided.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  14. Acceptance of Terms
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By completing purchase, Client confirms that they:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Have read this Agreement</li>
                  <li>Understand the terms</li>
                  <li>Agree to the 3-month minimum commitment</li>
                  <li>Authorize recurring billing</li>
                  <li>Agree to all policies stated herein</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  This electronic acceptance constitutes a legally binding agreement.
                </p>
              </section>


            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
